import { safeStringEquals } from './security';

export function caseInsensitiveSort(a, b) {
    return a?.localeCompare(b, 'en', {sensitivity: 'base'})
}

export function caseInsensitiveReverseSort(a, b) {
    return b?.localeCompare(a, 'en', {sensitivity: 'base'})
}

const operatorValuePattern = /^([<>])(\d+(\.\d+)?|\.\d+)(.*)$/
const numberValuePattern = /^(\d+(\.\d+)?|\.\d+)(.*)$/

// Define the parsed value types
type ParsedValue =
    | { type: 'operator'; operator: '<' | '>'; number: number; suffix: string }
    | { type: 'number'; number: number; suffix: string }
    | { type: 'invalid'; suffix: string } // For invalid values

type ParsedNumericValue = Exclude<ParsedValue, { type: 'invalid' }>

function isParsedNumericValue(value: ParsedValue): value is ParsedNumericValue {
    return value.type !== 'invalid'
}

function isCountUnitSuffix(suffix: string): boolean {
    const [firstToken = ''] = suffix.trim().toUpperCase().split(/\s+/)
    return safeStringEquals(firstToken, 'COUNT')
}

function compareCountBucket(left: ParsedValue, right: ParsedValue): number {
    if (!isParsedNumericValue(left) || !isParsedNumericValue(right)) {
        return 0
    }

    const leftIsCount = isCountUnitSuffix(left.suffix)
    const rightIsCount = isCountUnitSuffix(right.suffix)

    if (leftIsCount === rightIsCount) {
        return 0
    }

    return leftIsCount ? -1 : 1
}

function compareParsedNumericValues(left: ParsedValue, right: ParsedValue): number {
    if (left.type === 'operator' && right.type === 'operator') {
        if (left.operator === '<' && right.operator !== '<') return -1;
        if (left.operator === '>' && right.operator !== '>') return 1;
        if (right.operator === '<' && left.operator !== '<') return 1;
        if (right.operator === '>' && left.operator !== '>') return -1;

        if (left.number < right.number) return -1;
        if (left.number > right.number) return 1;
        return 0
    }

    if (left.type === 'operator') return -1
    if (right.type === 'operator') return 1

    if (left.type === 'number' && right.type === 'number') {
        if (left.number < right.number) return -1;
        if (left.number > right.number) return 1;
    }

    return 0
}

export function numericSortByText(
    a: { value: string } | string,
    b: { value: string } | string
): number {
    // Extract the text value from the input
    const textA = typeof a === 'string' ? a : a.value;
    const textB = typeof b === 'string' ? b : b.value;

    // Helper function to parse values
    const parseValue = (text: string): ParsedValue => {
        const trimmed = text.trim()
        const operatorMatch = trimmed.match(operatorValuePattern)
        const numberMatch = trimmed.match(numberValuePattern)

        if (operatorMatch) {
            const operator = operatorMatch[1] as '<' | '>' // Explicitly type the operator
            const number = parseFloat(operatorMatch[2])
            const suffix = operatorMatch[4].trim() // Extract suffix
            return { type: 'operator', operator, number, suffix }
        } else if (numberMatch) {
            const number = parseFloat(numberMatch[1])
            const suffix = numberMatch[3].trim() // Extract suffix
            return { type: 'number', number, suffix }
        }
        return { type: 'invalid', suffix: trimmed } // Return invalid type for unrecognized values
    }

    const parsedA = parseValue(textA)
    const parsedB = parseValue(textB)

    const bucketComparison = compareCountBucket(parsedA, parsedB)
    if (bucketComparison !== 0) {
        return bucketComparison
    }

    const comparison = compareParsedNumericValues(parsedA, parsedB)
    if (comparison !== 0) {
        return comparison
    }

    // Fallback for invalid values
    if (parsedA.type === 'invalid' || parsedB.type === 'invalid') {
        console.warn('Invalid value detected in numericSortByText:', { a, b })
    }
    return 0; // Treat invalid values as equal
}
